import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtRp } from "@/components/AmountInput";
import { useI18n } from "@/context/I18nContext";
import { fl } from "@/lib/finI18n";
import { tx } from "@/lib/txnI18n";
import { BookOpen } from "lucide-react";

const NUM = "px-2 py-1.5 num text-right whitespace-nowrap";

// Read-only double-entry view of a transaction, laid out like the General Journal form.
export function TxnJournal({ txnId, stamp }) {
  const { lang } = useI18n();
  const [j, setJ] = useState(null);
  useEffect(() => {
    if (!txnId) return;
    api.get(`/transactions/${txnId}/journal?lang=${lang}`).then(({ data }) => setJ(data)).catch(() => setJ(null));
  }, [txnId, lang, stamp]);
  if (!j || !j.lines.length) return null;
  return (
    <div className="p-5 border-b border-border space-y-2" data-testid="txn-journal-view">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" />{fl("jAuto", lang)}</p>
        {!j.posted && <span className="text-[10px] text-amber-600" data-testid="txn-journal-preview">{fl("jPreview", lang)}</span>}
      </div>
      <div className="rounded-md border border-border overflow-x-auto text-xs">
        <table className="w-full">
          <thead className="bg-secondary/50 text-[11px] text-muted-foreground">
            <tr>
              <th className="px-3 py-1.5 text-left font-normal">{fl("account", lang)}</th>
              <th className="px-2 py-1.5 text-left font-normal">{fl("ledger", lang)}</th>
              <th className="px-2 py-1.5 text-right font-normal">{fl("debit", lang)}</th>
              <th className="px-2 py-1.5 text-right font-normal">{fl("credit", lang)}</th>
            </tr>
          </thead>
          <tbody>
            {j.lines.map((l, i) => (
              <tr key={i} className="border-t border-border align-top" data-testid={`txn-journal-view-line-${i}`}>
                <td className={`py-1.5 pr-2 ${l.credit ? "pl-7" : "pl-3"}`}><span className="num text-muted-foreground mr-1">{l.account_code}</span>{l.account_name}</td>
                <td className="px-2 py-1.5 break-words text-muted-foreground" title={l.ledger || ""}>{l.ledger || "—"}
                  {l.note && <span className="block text-[10px] italic" data-testid={`txn-journal-view-note-${i}`}>{l.note}</span>}
                </td>
                <td className={NUM}>{l.debit ? fmtRp(l.debit, lang) : ""}</td>
                <td className={NUM}>{l.credit ? fmtRp(l.credit, lang) : ""}</td>
              </tr>
            ))}
            <tr className="border-t border-border bg-secondary/40 font-semibold">
              <td colSpan={2} className="px-3 py-1.5">{tx("total", lang)}</td>
              <td className={NUM} data-testid="txn-journal-view-debit">{fmtRp(j.total_debit, lang)}</td>
              <td className={NUM} data-testid="txn-journal-view-credit">{fmtRp(j.total_credit, lang)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
